import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowRight, Check, Phone } from "lucide-react";
import SEO from "../components/SEO";
import EditorialMarquee from "../components/EditorialMarquee";
import StatusBadge from "../components/StatusBadge";
import { FadeUp, MaskedLines, SectionLabel } from "../components/Reveal";
import { INFO_PAGES } from "../data/info-pages";
import { PHONE_DISPLAY, PHONE_TEL } from "../data/site";

export default function InfoPage() {
    const { pathname } = useLocation();
    const data = INFO_PAGES.find((p) => p.slug === pathname);
    if (!data) return <Navigate to="/" replace />;

    return (
        <div data-testid="info-page">
            <SEO title={data.metaTitle} description={data.metaDesc} path={data.slug} />

            <section className="grain relative overflow-hidden bg-midnight py-10 text-white md:py-16">
                <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-emergency/20 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <nav className="mb-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50" data-testid="breadcrumb">
                        <Link to="/" className="transition-colors hover:text-gold">Accueil</Link>
                        <span>/</span>
                        <span>Dépannage</span>
                        <span>/</span>
                        <span className="text-gold">{data.title}</span>
                    </nav>
                    <SectionLabel dark>{data.kicker}</SectionLabel>
                    <StatusBadge className="mt-4" />
                    <h1 className="mt-3 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tighter sm:text-5xl">
                        <MaskedLines delay={0.15} lines={[data.title]} />
                    </h1>
                    <FadeUp delay={0.5}>
                        <p className="mt-5 max-w-2xl leading-relaxed text-white/70">{data.intro}</p>
                        <div className="mt-7 flex flex-wrap gap-4">
                            <a
                                href={PHONE_TEL}
                                data-testid="info-call-button"
                                className="flex items-center gap-3 bg-emergency px-7 py-4 font-bold text-white transition-colors hover:bg-emergency-dark"
                            >
                                <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                            </a>
                            <Link
                                to="/devis-contact"
                                data-testid="info-devis-button"
                                className="flex items-center gap-2 border border-white/30 px-7 py-4 font-bold text-white transition-colors hover:border-gold hover:bg-gold hover:text-midnight"
                            >
                                Devis gratuit <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </FadeUp>
                </div>
            </section>

            <EditorialMarquee dark={false} />

            {data.checklist && (
                <section className="mx-auto max-w-7xl px-4 pt-14 sm:px-6 lg:px-8">
                    <div className="grid gap-px rounded-xl bg-midnight/10 sm:grid-cols-2 lg:grid-cols-3" data-testid="info-checklist">
                        {data.checklist.map((item, i) => (
                            <FadeUp key={item} delay={(i % 6) * 0.04} className="bg-white">
                                <div className="flex h-full items-center gap-3 px-5 py-4 text-sm font-semibold text-midnight/80">
                                    <Check className="h-4 w-4 shrink-0 text-emergency" /> {item}
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </section>
            )}

            <section className="mx-auto max-w-7xl space-y-16 px-4 py-14 sm:px-6 md:py-20 lg:px-8">
                {data.sections.map((s, i) =>
                    s.img ? (
                        <div key={s.h} className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                            <FadeUp className={i % 2 === 1 ? "lg:order-2" : ""}>
                                <div className="flex aspect-[4/3] w-full items-center justify-center rounded-xl bg-midnight/5 p-4 shadow-[0_24px_60px_-20px_rgba(10,17,40,0.28)]">
                                    <img
                                        src={s.img}
                                        alt={`${data.title} — Serrurier Paris`}
                                        className="h-full w-full object-contain"
                                        width="800"
                                        height="600"
                                        loading="lazy"
                                    />
                                </div>
                            </FadeUp>
                            <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                                <FadeUp>
                                    <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-4xl">{s.h}</h2>
                                    {s.ps.map((p, j) => (
                                        <p key={j} className="mt-4 leading-relaxed text-midnight/70">{p}</p>
                                    ))}
                                </FadeUp>
                            </div>
                        </div>
                    ) : (
                        <FadeUp key={s.h}>
                            <div className="max-w-3xl">
                                <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-4xl">{s.h}</h2>
                                {s.ps.map((p, j) => (
                                    <p key={j} className="mt-4 leading-relaxed text-midnight/70">{p}</p>
                                ))}
                            </div>
                        </FadeUp>
                    )
                )}
            </section>

            <section className="grain bg-midnight py-14 text-white md:py-16">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-4 sm:px-6 lg:px-8">
                    <div>
                        <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                            Une question, une urgence ?
                        </h2>
                        <p className="mt-3 text-white/60">Devis gratuit au téléphone — intervention en 30 minutes, 24h/24.</p>
                    </div>
                    <a
                        href={PHONE_TEL}
                        data-testid="info-bottom-call-button"
                        className="flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                    >
                        <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                    </a>
                </div>
            </section>
        </div>
    );
}
